import ReactECharts from 'echarts-for-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { LuBot } from 'react-icons/lu';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { UserStatsV2 } from '../../entities/analytics-v2';
import { getProviderCapabilities } from '../../entities/provider';
import { getUsersStatsV2 } from '../../shared/api/dashboard';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjectById } from '../../shared/contexts/Projects.provider';
import { asISODate, fmt1, fmtDaysFromHours, safeNum } from '../../shared/utils/stat-helpers';
import { InfoTip, KpiCard, StatEmpty, StatError, StatLoading } from '../../widgets/stats';

function median(values: number[]) {
  const a = [...values].sort((x, y) => x - y);
  if (!a.length) return 0;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

type MetricKey =
  | 'completedCount'
  | 'activeTasks'
  | 'reopenEvents'
  | 'avgCycleTimeHours'
  | 'reopenRatePercent'
  | 'loadPercent'
  | 'flowEfficiencyPercent';

const METRICS: { key: MetricKey; label: string; format: (v: number) => string; tip: string }[] = [
  {
    key: 'completedCount',
    label: 'Completed',
    format: (v) => String(Math.round(v)),
    tip: 'Completed tasks in selected range.',
  },
  {
    key: 'activeTasks',
    label: 'Active',
    format: (v) => String(Math.round(v)),
    tip: 'Active tasks snapshot (current open workload).',
  },
  {
    key: 'reopenEvents',
    label: 'Reopens',
    format: (v) => String(Math.round(v)),
    tip: 'Raw count of reopen events in the range.',
  },
  {
    key: 'avgCycleTimeHours',
    label: 'Avg cycle',
    format: (v) => fmtDaysFromHours(v),
    tip: 'Average cycle time (created → done). Computed from changelog.',
  },
  {
    key: 'reopenRatePercent',
    label: 'Reopen %',
    format: (v) => `${fmt1(v)}%`,
    tip: 'Reopen rate = reopenEvents / completedCount. Lower is better.',
  },
  {
    key: 'loadPercent',
    label: 'Load',
    format: (v) => `${fmt1(v)}%`,
    tip: 'Load approximation. Use it to spot overloaded or underloaded people.',
  },
  {
    key: 'flowEfficiencyPercent',
    label: 'Flow eff.',
    format: (v) => (v > 0 ? `${fmt1(v)}%` : '—'),
    tip: 'Flow efficiency = time in active statuses / total cycle time (project-level proxy). Industry avg: 15–40%.',
  },
];

type SortKey = 'userName' | 'role' | MetricKey;
type SortDir = 'asc' | 'desc';

function rateColor(pct: number, invert = false): string {
  const good = invert ? pct <= 10 : pct >= 80;
  const warn = invert ? pct <= 30 : pct >= 50;
  if (good) return 'text-success';
  if (warn) return 'text-warning';
  return 'text-error';
}

export const UsersStatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const dateRangeCtx = useDateRange();
  const setDashHeader = useSetDashboardHeader();
  const { openChat } = useAIChat();
  const project = useProjectById(projectId);
  const supportsUsers = getProviderCapabilities(project?.provider).supportsUsers;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [data, setData] = useState<UserStatsV2[]>([]);

  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [metric, setMetric] = useState<MetricKey>('completedCount');
  const [topN, setTopN] = useState<number>(10);

  const [sortKey, setSortKey] = useState<SortKey>('completedCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    const run = async () => {
      if (!projectId || !supportsUsers) {
        setLoading(false);
        return;
      }
      if (!dateRangeCtx?.from || !dateRangeCtx?.to) return;

      try {
        setLoading(true);
        setError(null);

        const range = { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) };
        const { stat } = await getUsersStatsV2(projectId, range);
        setData(stat ?? []);
      } catch (e) {
        console.error(e);
        setError('Failed to load users stats.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [projectId, dateRangeCtx?.from, dateRangeCtx?.to, supportsUsers]);

  const roles = useMemo(() => {
    const set = new Set<string>();
    for (const u of data) if (u.role) set.add(u.role);
    return ['all', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((u) => {
      const name = (u.userName ?? '').toLowerCase();
      const id = (u.userId ?? '').toLowerCase();
      const matchesQuery = !q || name.includes(q) || id.includes(q);
      const matchesRole = roleFilter === 'all' ? true : u.role === roleFilter;
      return matchesQuery && matchesRole;
    });
  }, [data, query, roleFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;

    const getValue = (u: UserStatsV2): string | number => {
      if (sortKey === 'userName') return u.userName ?? '';
      if (sortKey === 'role') return u.role ?? '';
      return safeNum((u as any)[sortKey], 0);
    };

    return [...filtered].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const summary = useMemo(() => {
    const users = filtered.length;
    const completed = filtered.reduce((s, u) => s + safeNum(u.completedCount), 0);
    const active = filtered.reduce((s, u) => s + safeNum(u.activeTasks), 0);
    const reopens = filtered.reduce((s, u) => s + safeNum(u.reopenEvents), 0);

    const reopenArr = filtered.map((u) => safeNum(u.reopenRatePercent));
    const loadArr = filtered.map((u) => safeNum(u.loadPercent));
    const cycleArr = filtered.map((u) => safeNum(u.avgCycleTimeHours) / 24);

    return {
      users,
      completed,
      active,
      reopens,
      reopenMedian: median(reopenArr),
      loadMedian: median(loadArr),
      cycleMedianDays: median(cycleArr),
    };
  }, [filtered]);

  const chartData = useMemo(() => {
    const metricMeta = METRICS.find((m) => m.key === metric)!;

    const rows = sorted
      .map((u) => {
        const value = safeNum((u as any)[metric], 0);
        return {
          userId: u.userId,
          name: u.userName || u.userId,
          value,
          valueLabel: metricMeta.format(value),
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, topN);

    const values = rows.map((r) => r.value);
    const med = median(values);

    return { rows, medianValue: med, metricMeta };
  }, [sorted, metric, topN]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'userName' || key === 'role' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <span className="opacity-20 ml-1">↕</span>;
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const topUsersOption = useMemo(() => {
    const labels = chartData.rows.map((r) => r.name);
    const values = chartData.rows.map((r) => r.value);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const p = params?.[0];
          const v = safeNum(p?.value);
          return `<b>${p?.axisValue}</b><br/>${chartData.metricMeta.label}: ${chartData.metricMeta.format(v)}`;
        },
      },
      grid: { left: 20, right: 16, top: 10, bottom: 70, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { rotate: 18, interval: 0 },
      },
      yAxis: { type: 'value' },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: 18,
          label: {
            show: true,
            position: 'top',
            formatter: (p: any) => chartData.metricMeta.format(safeNum(p?.value)),
          },
          markLine: {
            silent: true,
            data: [{ yAxis: chartData.medianValue }],
            lineStyle: { type: 'dashed' },
            label: { formatter: `median: ${chartData.metricMeta.format(chartData.medianValue)}` },
          },
          itemStyle: { borderRadius: [8, 8, 0, 0] },
        },
      ],
    };
  }, [chartData]);

  const goToUser = (userId: string) => {
    navigate(`/dashboard/projects/${projectId}/users/${userId}`);
  };

  useEffect(() => {
    setDashHeader({
      component: 'users',
      breadcrumb: (
        <Link to={`/dashboard/projects/${projectId}`} className="btn btn-sm btn-ghost px-0 h-auto min-h-0">
          <Tooltip message={'Project'} position={'bottom'}>
            <span className="text-xl">←</span>
          </Tooltip>
        </Link>
      ),
      title: 'Users stats',
      actions: (
        <button
          className="btn btn-primary btn-sm shadow-glow gap-1.5"
          onClick={() =>
            openChat({
              projectId,
              suggestedPrompts: [
                'Who is overloaded on this project?',
                'Which users have quality issues?',
                'Who has the slowest cycle time and why?',
                'Summarize team health for this period',
              ],
              analyticsContext: {
                scope: 'users',
                projectIds: projectId ? [projectId] : undefined,
                dateRange:
                  dateRangeCtx?.from && dateRangeCtx?.to
                    ? {
                        from: dateRangeCtx.from.toISOString().split('T')[0],
                        to: dateRangeCtx.to.toISOString().split('T')[0],
                      }
                    : undefined,
              },
            })
          }
        >
          <LuBot size={14} />
          Ask mFlow
        </button>
      ),
    });
    return () => setDashHeader({});
  }, [projectId, setDashHeader]);

  if (loading) return <StatLoading />;
  if (!supportsUsers) return <StatEmpty message="Per-contributor stats aren't available for this provider yet." />;
  if (error) return <StatError message={error} />;

  return (
    <div className="w-full p-4 space-y-4">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <input
          className="input input-bordered w-full md:w-72 bg-base-100"
          placeholder="Search by name or id…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="select select-bordered w-full md:w-56 bg-base-100"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          {roles.map((r) => (
            <option key={r} value={r}>
              {r === 'all' ? 'All roles' : r}
            </option>
          ))}
        </select>
        <div className="text-sm opacity-70">
          Showing <span className="font-medium">{filtered.length}</span> of{' '}
          <span className="font-medium">{data.length}</span> users
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label={
            <>
              Completed
              <InfoTip text={'Sum of completed tasks for all filtered users in the selected range.'} />
            </>
          }
          value={summary.completed}
          footer={`${summary.users} user${summary.users !== 1 ? 's' : ''}`}
        />

        <KpiCard
          label={
            <>
              Active tasks
              <InfoTip text={'Sum of open tasks across filtered users (current snapshot).'} />
            </>
          }
          value={summary.active}
          footer="Open workload"
        />

        <KpiCard
          label={
            <>
              Reopens
              <InfoTip text={'Total reopen events across filtered users. Indicates rework or unclear specs.'} />
            </>
          }
          value={summary.reopens}
          footer={`median reopen rate: ${fmt1(summary.reopenMedian)}%`}
        />

        <KpiCard
          label={
            <>
              Team medians
              <InfoTip text={'Median values across filtered users — useful to see what "typical" looks like.'} />
            </>
          }
        >
          <div className="text-sm mt-1 space-y-0.5">
            <div>
              Cycle: <span className="font-medium">{fmt1(summary.cycleMedianDays)}d</span>
            </div>
            <div>
              Load: <span className="font-medium">{fmt1(summary.loadMedian)}%</span>
            </div>
          </div>
        </KpiCard>
      </div>

      {/* Chart card */}
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-4 space-y-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <div className="text-lg font-semibold flex items-center">
                Top users by metric
                <InfoTip text={chartData.metricMeta.tip} />
              </div>
              <div className="text-sm opacity-70">
                Metric: <span className="font-medium">{chartData.metricMeta.label}</span>
                {' · '}
                Median: <span className="font-medium">{chartData.metricMeta.format(chartData.medianValue)}</span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <select
                className="select select-bordered bg-base-100"
                value={metric}
                onChange={(e) => setMetric(e.target.value as MetricKey)}
              >
                {METRICS.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>

              <select
                className="select select-bordered bg-base-100"
                value={String(topN)}
                onChange={(e) => setTopN(Number(e.target.value))}
              >
                {[5, 10, 15, 20].map((n) => (
                  <option key={n} value={n}>
                    Top {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="h-[320px]">
            <ReactECharts
              option={topUsersOption}
              style={{ height: '100%' }}
              onEvents={{
                click: (p: any) => {
                  const row = chartData.rows[p.dataIndex];
                  if (row) goToUser(row.userId);
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-0">
          <div className="p-4 flex items-center justify-between">
            <div className="text-lg font-semibold">All users</div>
            <button
              className="btn btn-sm btn-ghost opacity-60"
              onClick={() => {
                setQuery('');
                setRoleFilter('all');
              }}
            >
              Reset filters
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="table table-zebra table-sm w-full">
              <thead>
                <tr className="text-xs uppercase opacity-60">
                  <th className="cursor-pointer" onClick={() => toggleSort('userName')}>
                    User {sortIcon('userName')}
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('completedCount')}>
                    Done {sortIcon('completedCount')}
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('activeTasks')}>
                    Active {sortIcon('activeTasks')}
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('reopenEvents')}>
                    <span title="Raw reopen event count">Reopens {sortIcon('reopenEvents')}</span>
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('reopenRatePercent')}>
                    <span title="Reopen rate = reopenEvents / completedCount">
                      Reopen % {sortIcon('reopenRatePercent')}
                    </span>
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('avgCycleTimeHours')}>
                    <span title="Average cycle time (created → done)">Cycle {sortIcon('avgCycleTimeHours')}</span>
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('loadPercent')}>
                    <span title="Load approximation">Load {sortIcon('loadPercent')}</span>
                  </th>
                  <th className="cursor-pointer text-right" onClick={() => toggleSort('flowEfficiencyPercent')}>
                    <span title="Flow efficiency = time in active statuses / total cycle time (project-level proxy)">
                      Flow eff. {sortIcon('flowEfficiencyPercent')}
                    </span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {sorted.map((u) => {
                  return (
                    <tr key={u.userId} className="hover cursor-pointer" onClick={() => goToUser(u.userId)}>
                      <td className="py-2">
                        <div className="flex flex-col">
                          <span className="font-medium">{u.userName || '—'}</span>
                          {u.role && (
                            <span className="text-xs opacity-50 badge badge-ghost badge-sm mt-0.5 w-fit">{u.role}</span>
                          )}
                        </div>
                      </td>

                      <td className="text-right font-medium">{safeNum(u.completedCount)}</td>
                      <td className="text-right">{safeNum(u.activeTasks)}</td>
                      <td className="text-right">{safeNum(u.reopenEvents)}</td>

                      <td className={`text-right ${rateColor(u.reopenRatePercent, true)}`}>
                        {fmt1(u.reopenRatePercent)}%
                      </td>

                      <td className="text-right">{fmtDaysFromHours(u.avgCycleTimeHours)}</td>

                      <td className="text-right">
                        <span
                          className={u.loadPercent >= 90 ? 'text-error' : u.loadPercent >= 70 ? 'text-warning' : ''}
                        >
                          {fmt1(u.loadPercent)}%
                        </span>
                      </td>

                      <td className="text-right">
                        {u.flowEfficiencyPercent > 0 ? (
                          <span
                            className={
                              u.flowEfficiencyPercent > 60
                                ? 'text-success'
                                : u.flowEfficiencyPercent < 15
                                  ? 'text-warning'
                                  : ''
                            }
                          >
                            {fmt1(u.flowEfficiencyPercent)}%
                          </span>
                        ) : (
                          <span className="opacity-30 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!sorted.length && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center opacity-60">
                      No users match current filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
